import axios, { AxiosError } from "axios"
import { getLocaliceKeyFromErrorCode } from "../services/error-codes"
import HeapsError from "./heaps-error"
import { TFunction } from "i18next"
import { useEffect } from "react"
import { useGlobalModals } from "../context/GlobalModalsContext"
import { useI18next } from "gatsby-plugin-react-i18next"
import { routes } from "../routes"
import { translationKeys } from "./translationKeys"
import { captureException } from "../services/exception-logger"

type Error = {
  code: string
  detail?: string
}

// this is format that comes from our backend
type ErrorResponse = {
  errors: Error[]
}

const fallBackErrorMessage = "An unexpected error occurred"

export const extractErrorMessageErrorCodeAndTranslationKey = (
  error: unknown
) => {
  const fallbackError = {
    errorMessage: fallBackErrorMessage,
    translationKey: "",
    errorCode: "unknown_error",
  }

  if (!axios.isAxiosError(error)) {
    // unexpected error occurred
    return fallbackError
  }

  // https://axios-http.com/docs/handling_errors
  // See above for handling errors
  const typedError = error as AxiosError
  const errorResponse = typedError.response
  const errMessage = typedError.message

  const hintedFallbackError = {
    ...fallbackError,
    errorMessage: errMessage || fallBackErrorMessage,
  }

  if (errorResponse) {
    const errorResponseData = errorResponse.data as ErrorResponse | undefined
    if (!errorResponseData?.errors || !errorResponseData?.errors.length) {
      return hintedFallbackError
    }
    const errorCode = errorResponseData.errors[0].code
    const errorDetail = errorResponseData.errors[0].detail
    const localiceKey = getLocaliceKeyFromErrorCode(errorCode)
    // prio for error message: 1) detail, 2) translation key, 3) general fallback
    const errorMessage = errorDetail ? errorDetail : ""
    const translationKey = errorMessage ? "" : localiceKey ? localiceKey : ""
    return { errorMessage, translationKey, errorCode }
  }

  return hintedFallbackError
}

export const getErrorMessage = (error: unknown, t: TFunction) => {
  if (error instanceof HeapsError) {
    if (error.errorMessage) {
      return error.errorMessage
    }
    if (error.translationKey) {
      return t(error.translationKey)
    }
  }

  // Let's log and return a fallback message. This is not something we called ourselves
  captureException(error, {
    tags: {
      errorSource: "GetErrorMessage - Not HeapsError",
    },
  })
  return fallBackErrorMessage
}

export const useHandleFetchError = (
  error: unknown,
  t: TFunction,
  navigateTo404: boolean = true
) => {
  const { setInfoModalConfig } = useGlobalModals()
  const { navigate } = useI18next()

  return useEffect(() => {
    if (!error) {
      return
    }
    const errorMessage = getErrorMessage(error, t)
    setInfoModalConfig({
      title: t(translationKeys.comboPaymentErrorGenericHeader),
      description: errorMessage,
      onOkayClick: () => {
        if (!navigateTo404) {
          return
        }
        navigate(routes.notFound)
      },
    })
  }, [error])
}
